import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

// ── Admin-only BDC editor ───────────────────────────────────────────────────
// Two tabs:
//  (1) "Static" — instrument-level fields (management_type, par/notes)
//  (2) "Quarters" — per-quarter NAV / NII / D-E / non-accruals / PIK / div paid
//
// Single source of truth: the instrument row + bdc_fundamentals rows.
// Admin overrides on instrument fields are protected from Finnhub re-enrichment.

const STATIC_FIELDS = [
  { key: "management_type",                label: "Management",                  type: "select",
    options: [["", "—"], ["internal", "Internal"], ["external", "External"]] },
  { key: "consecutive_dividend_increases", label: "Years of div growth",         type: "number", step: 1, min: 0 },
  { key: "notes",                          label: "Notes (free-form)",           type: "textarea" },
];

const QUARTER_FIELDS = [
  { key: "nav_per_share",            label: "NAV / share ($)",                step: 0.01 },
  { key: "nii_per_share",            label: "NII / share ($)",                step: 0.01 },
  { key: "dividend_paid_per_share",  label: "Dividend paid / share ($)",      step: 0.01 },
  { key: "debt_to_equity",           label: "Debt / Equity",                  step: 0.01 },
  { key: "non_accrual_pct",          label: "Non-accrual %",                  step: 0.01 },
  { key: "pik_income_pct",           label: "PIK income %",                   step: 0.1 },
];

function currentQuarterEnd() {
  // Default to most-recent completed quarter end. e.g. May 2026 → 2026-03-31
  const d = new Date();
  const m = d.getMonth();  // 0-indexed
  const y = d.getFullYear();
  if (m <= 2)  return `${y - 1}-12-31`;
  if (m <= 5)  return `${y}-03-31`;
  if (m <= 8)  return `${y}-06-30`;
  return `${y}-09-30`;
}

export default function BDCEditDialog({ ticker, onClose, onSaved }) {
  const [tab, setTab] = useState("static");
  const [staticVals, setStaticVals] = useState({});
  const [quarters, setQuarters] = useState([]);
  const [newQuarter, setNewQuarter] = useState({ quarter_end: currentQuarterEnd() });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [inst, q] = await Promise.all([
          api.get(`/admin/instruments/${ticker}`),
          api.get(`/admin/bdc/${ticker}/quarters`),
        ]);
        setStaticVals({
          management_type: inst.data.management_type || "",
          consecutive_dividend_increases: inst.data.consecutive_dividend_increases ?? "",
          notes: inst.data.notes || "",
        });
        setQuarters(q.data || []);
      } catch (e) {
        toast.error("Failed to load BDC data");
      } finally { setLoading(false); }
    })();
  }, [ticker]);

  const saveStatic = async () => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(staticVals).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        patch[k] = typeof v === "string" && !isNaN(parseFloat(v)) && k === "consecutive_dividend_increases"
          ? parseInt(v, 10) : v;
      });
      await api.put(`/admin/instruments/${ticker}`, patch);
      toast.success("Static fields saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveQuarter = async (q_end, vals) => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(vals).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        const f = QUARTER_FIELDS.find((x) => x.key === k);
        patch[k] = f ? parseFloat(v) : v;
      });
      await api.put(`/admin/bdc/${ticker}/quarters/${q_end}`, patch);
      const { data } = await api.get(`/admin/bdc/${ticker}/quarters`);
      setQuarters(data || []);
      setNewQuarter({ quarter_end: currentQuarterEnd() });
      toast.success(`Quarter ${q_end} saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteQuarter = async (q_end) => {
    if (!window.confirm(`Delete quarter ${q_end}?`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/bdc/${ticker}/quarters/${q_end}`);
      setQuarters((qs) => qs.filter((x) => x.quarter_end !== q_end));
      toast.success("Deleted");
      onSaved?.();
    } catch (e) {
      toast.error("Delete failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl bg-champagne border border-stone" data-testid="bdc-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-graphite">
            <span className="text-paars-dark">{ticker}</span> · Admin
          </DialogTitle>
        </DialogHeader>

        <div className="flex border-b border-stone -mt-2">
          <button data-testid="bdc-tab-static" onClick={() => setTab("static")}
            className={`px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold ${tab === "static" ? "text-paars-dark border-b-2 border-paars-dark" : "text-graphite/55 hover:text-graphite"}`}>Static</button>
          <button data-testid="bdc-tab-quarters" onClick={() => setTab("quarters")}
            className={`px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold ${tab === "quarters" ? "text-paars-dark border-b-2 border-paars-dark" : "text-graphite/55 hover:text-graphite"}`}>Quarters ({quarters.length})</button>
        </div>

        {loading ? (
          <div className="text-graphite/55 text-sm font-mono py-8 text-center">Loading…</div>
        ) : tab === "static" ? (
          <div className="space-y-4 py-2">
            {STATIC_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/60 mb-1">{f.label}</label>
                {f.type === "select" ? (
                  <select
                    data-testid={`bdc-static-${f.key}`}
                    value={staticVals[f.key] || ""}
                    onChange={(e) => setStaticVals((v) => ({ ...v, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone bg-champagne text-sm focus:outline-none focus:border-paars-dark"
                  >
                    {f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    data-testid={`bdc-static-${f.key}`}
                    value={staticVals[f.key] || ""}
                    onChange={(e) => setStaticVals((v) => ({ ...v, [f.key]: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-stone bg-champagne text-sm focus:outline-none focus:border-paars-dark"
                  />
                ) : (
                  <input
                    data-testid={`bdc-static-${f.key}`}
                    type={f.type || "text"}
                    step={f.step}
                    min={f.min}
                    value={staticVals[f.key] ?? ""}
                    onChange={(e) => setStaticVals((v) => ({ ...v, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone bg-champagne text-sm focus:outline-none focus:border-paars-dark"
                  />
                )}
              </div>
            ))}
            <div className="flex justify-end pt-2">
              <button data-testid="bdc-save-static" disabled={saving} onClick={saveStatic}
                className="px-5 py-2 bg-paars-dark text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite disabled:opacity-50">
                {saving ? "Saving…" : "Save Static"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {/* Add new quarter inline */}
            <NewQuarterForm
              defaults={newQuarter}
              onChange={setNewQuarter}
              onSave={(q_end, vals) => saveQuarter(q_end, vals)}
              saving={saving}
            />
            {/* Existing quarters */}
            {quarters.length === 0 ? (
              <div className="text-graphite/55 text-xs font-mono text-center py-6 border border-dashed border-stone">
                No quarterly fundamentals on record yet.
              </div>
            ) : (
              <div className="border border-stone">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-stone/60 border-b border-stone text-[10px] tracking-widest uppercase">
                      <th className="px-3 py-2 text-left">Quarter</th>
                      <th className="px-3 py-2 text-right">NAV</th>
                      <th className="px-3 py-2 text-right">NII</th>
                      <th className="px-3 py-2 text-right">Div Paid</th>
                      <th className="px-3 py-2 text-right">D/E</th>
                      <th className="px-3 py-2 text-right">Non-Acc</th>
                      <th className="px-3 py-2 text-right">PIK</th>
                      <th className="px-3 py-2 text-right">Source</th>
                      <th className="px-3 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quarters.map((q) => (
                      <tr key={q.quarter_end} data-testid={`bdc-quarter-${q.quarter_end}`} className="border-b border-stone/40 font-mono">
                        <td className="px-3 py-2">{q.quarter_end}</td>
                        <td className="px-3 py-2 text-right">{q.nav_per_share != null ? `$${q.nav_per_share}` : "—"}</td>
                        <td className="px-3 py-2 text-right">{q.nii_per_share != null ? `$${q.nii_per_share}` : "—"}</td>
                        <td className="px-3 py-2 text-right">{q.dividend_paid_per_share != null ? `$${q.dividend_paid_per_share}` : "—"}</td>
                        <td className="px-3 py-2 text-right">{q.debt_to_equity ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{q.non_accrual_pct != null ? `${q.non_accrual_pct}%` : "—"}</td>
                        <td className="px-3 py-2 text-right">{q.pik_income_pct != null ? `${q.pik_income_pct}%` : "—"}</td>
                        <td className="px-3 py-2 text-right text-graphite/50">{q.source}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => deleteQuarter(q.quarter_end)} className="p-1 text-graphite/45 hover:text-burgundy">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewQuarterForm({ defaults, onChange, onSave, saving }) {
  const [vals, setVals] = useState({});
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const valid = useMemo(() => {
    if (!defaults.quarter_end) return false;
    return Object.values(vals).some((v) => v !== "" && v != null);
  }, [defaults.quarter_end, vals]);

  return (
    <div className="border border-paars/50 bg-paars/5 p-4 space-y-3" data-testid="bdc-new-quarter">
      <div className="flex items-center gap-2">
        <Plus className="w-4 h-4 text-paars-dark" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-paars-dark">Add a quarter</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest font-bold text-graphite/60 mb-1">Quarter end</label>
          <input
            data-testid="bdc-new-quarter-date"
            type="date"
            value={defaults.quarter_end}
            onChange={(e) => onChange({ ...defaults, quarter_end: e.target.value })}
            className="w-full px-3 py-2 border border-stone bg-champagne text-sm"
          />
        </div>
        {QUARTER_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-graphite/60 mb-1">{f.label}</label>
            <input
              data-testid={`bdc-new-${f.key}`}
              type="number"
              step={f.step}
              value={vals[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
              className="w-full px-3 py-2 border border-stone bg-champagne text-sm font-mono"
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end pt-1">
        <button
          data-testid="bdc-new-quarter-save"
          disabled={!valid || saving}
          onClick={() => { onSave(defaults.quarter_end, vals); setVals({}); }}
          className="px-5 py-2 bg-paars-dark text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Quarter"}
        </button>
      </div>
    </div>
  );
}
